///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// "My Datasets" cloud storage management: quota bar, dataset list with
/// open-on-this-device and delete. Datasets are uploaded from the dataset
/// browser's "Save to cloud" button after dropping a CSV/GeoJSON file.
///

import { openCloudDataset } from "@/components/features/user-datasets/openCloudDataset"
import { useToast } from "@/components/ui/use-toast"
import { formatBytes } from "@/earthbrowser/utils/format"
import { setProfileOpen } from "@/redux/dialogSlice"
import { shareUrlForSlug, useCreateShareMutation, useDeleteShareMutation, useListSharesQuery } from "@/redux/sharesApi"
import { useAppDispatch, useAppStore } from "@/redux/store"
import { useDeleteUserDatasetMutation, useListUserDatasetsQuery } from "@/redux/userDatasetsApi"
import { relativeTime } from "@/utils/savedViews"
import { cn } from "@/utils/utils"
import type { ShareRecord, UserDatasetRecord } from "@shared-types"
import { Check, Eye, FileJson, FileSpreadsheet, Globe, Image, Link2, Loader2, MapPin, Trash2 } from "lucide-react"
import { useState } from "react"

export function StorageSection() {
	const { data, isLoading, isError } = useListUserDatasetsQuery()

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-8 text-muted-foreground">
				<Loader2 className="w-5 h-5 animate-spin" />
			</div>
		)
	}
	if (isError || !data) {
		return (
			<p className="py-6 text-sm text-muted-foreground text-center">
				Couldn't load your datasets. Check your connection and try again.
			</p>
		)
	}

	const { datasets, quota } = data
	const usedFraction = quota.max_storage_bytes > 0
		? Math.min(1, quota.used_bytes / quota.max_storage_bytes) : 0

	return (
		<div className="space-y-4">
			<div className="space-y-1.5">
				<div className="flex items-baseline justify-between text-sm">
					<span className="text-foreground">
						{formatBytes(quota.used_bytes)} of {formatBytes(quota.max_storage_bytes)} used
					</span>
					<span className="text-xs text-muted-foreground">
						{quota.dataset_count} of {quota.max_datasets} datasets
					</span>
				</div>
				<div className="h-2 rounded-full bg-muted overflow-hidden">
					<div
						className={cn(
							"h-full rounded-full transition-all",
							usedFraction > 0.9 ? "bg-red-500" : usedFraction > 0.7 ? "bg-amber-500" : "bg-cyan-500"
						)}
						style={{ width: `${Math.max(usedFraction * 100, quota.used_bytes > 0 ? 2 : 0)}%` }}
					/>
				</div>
				<p className="text-xs text-muted-foreground">
					Files up to {formatBytes(quota.max_dataset_size_bytes)} each
				</p>
			</div>

			{datasets.length === 0 ? (
				<p className="py-6 text-sm text-muted-foreground text-center">
					No datasets yet — drop a CSV or GeoJSON file onto the globe, then use
					the cloud button in its data browser to save it here.
				</p>
			) : (
				<div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
					{datasets.map(dataset => (
						<DatasetRow key={dataset.id} dataset={dataset} />
					))}
				</div>
			)}

			<SharedLinksSection />
		</div>
	)
}

/**
 * Share link management: every link the account has created (views,
 * placemarks, datasets) with view counts, copy, and revoke.
 */
function SharedLinksSection() {
	const { data } = useListSharesQuery()
	if (!data || data.shares.length === 0) return null

	return (
		<div className="space-y-2 pt-2">
			<div className="flex items-baseline justify-between">
				<h4 className="text-sm font-medium text-foreground">Shared links</h4>
				<span className="text-xs text-muted-foreground">
					{data.shares.length} of {data.limits.max_shares}
				</span>
			</div>
			<div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
				{data.shares.map(share => (
					<ShareRow key={share.slug} share={share} />
				))}
			</div>
		</div>
	)
}

const cShareTypeLabel: Record<ShareRecord["share_type"], string> = {
	view: "View",
	placemark: "Place",
	dataset: "Dataset",
	story: "Story",
}

function ShareRow({ share }: { share: ShareRecord }) {
	const [deleteShare, { isLoading: deleting }] = useDeleteShareMutation()
	const [copied, setCopied] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)

	const expired = share.expires_at !== null && Date.parse(share.expires_at) < Date.now()

	const handleCopy = () => {
		void navigator.clipboard.writeText(shareUrlForSlug(share.slug)).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		})
	}

	const handleDelete = () => {
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			setTimeout(() => setConfirmingDelete(false), 3000)
			return
		}
		void deleteShare(share.slug)
	}

	return (
		<div className={cn("flex items-center gap-3 px-3 py-2", expired && "opacity-60")}>
			<div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
				{share.share_type === "placemark"
					? <MapPin className="w-4 h-4 text-blue-400" />
					: share.share_type === "dataset"
						? <FileJson className="w-4 h-4 text-blue-400" />
						: <Globe className="w-4 h-4 text-blue-400" />}
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-foreground truncate">{share.title}</div>
				<div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
					<span>{cShareTypeLabel[share.share_type]}</span>
					<span>· {relativeTime(share.created_at)}</span>
					<span className="inline-flex items-center gap-0.5">
						· <Eye className="w-3 h-3" /> {share.view_count.toLocaleString()}
					</span>
					{expired && <span className="text-amber-500">· expired</span>}
					{!expired && share.expires_at && (
						<span>· expires {new Date(share.expires_at).toLocaleDateString()}</span>
					)}
				</div>
			</div>
			<button
				onClick={handleCopy}
				title={copied ? "Link copied" : "Copy share link"}
				aria-label={`Copy link for ${share.title}`}
				className={cn(
					"flex-shrink-0 p-2 rounded-lg transition-colors",
					copied ? "text-emerald-400" : "text-muted-foreground hover:text-foreground hover:bg-muted"
				)}
			>
				{copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
			</button>
			<button
				onClick={handleDelete}
				disabled={deleting}
				title={confirmingDelete ? "Click again to revoke this link" : "Revoke share link"}
				aria-label={confirmingDelete ? `Confirm revoke ${share.title}` : `Revoke ${share.title}`}
				className={cn(
					"flex-shrink-0 p-2 rounded-lg transition-colors disabled:opacity-50",
					confirmingDelete
						? "text-red-500 bg-red-500/10"
						: "text-muted-foreground hover:text-red-400 hover:bg-muted"
				)}
			>
				{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
			</button>
		</div>
	)
}

function DatasetRow({ dataset }: { dataset: UserDatasetRecord }) {
	const dispatch = useAppDispatch()
	const store = useAppStore()
	const { toast } = useToast()
	const [deleteDataset, { isLoading: deleting }] = useDeleteUserDatasetMutation()
	const [createShare, { isLoading: sharing }] = useCreateShareMutation()
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const [opening, setOpening] = useState(false)
	const [shareCopied, setShareCopied] = useState(false)

	const pending = dataset.status === "pending"
	// Sharing requires server content validation (moderation prerequisite)
	const shareable = dataset.status === "active" && dataset.validated

	const handleShare = async () => {
		if (sharing || !shareable) return
		try {
			const result = await createShare({
				share_type: "dataset",
				title: dataset.name,
				// Only dataset_id is read; the server rebuilds the payload
				payload: {
					dataset_id: dataset.id,
					name: dataset.name,
					filename: dataset.filename,
					format: dataset.format,
					cdn_url: dataset.cdn_url,
					size_bytes: dataset.size_bytes,
					feature_count: dataset.feature_count,
				},
			}).unwrap()
			await navigator.clipboard.writeText(shareUrlForSlug(result.share.slug))
			setShareCopied(true)
			setTimeout(() => setShareCopied(false), 1500)
		} catch (error) {
			const message = (error as { data?: { message?: string } })?.data?.message
			toast({
				title: "Couldn't share dataset",
				description: message ?? "Share failed",
				variant: "destructive",
			})
		}
	}

	const handleOpen = async () => {
		if (opening || pending) return
		setOpening(true)
		try {
			await openCloudDataset(dataset, dispatch, () => store.getState())
			// The dataset window opened behind the dialog
			dispatch(setProfileOpen(false))
		} catch (error) {
			toast({
				title: "Couldn't open dataset",
				description: error instanceof Error ? error.message : "Download failed",
				variant: "destructive",
			})
		} finally {
			setOpening(false)
		}
	}

	const handleDelete = () => {
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			setTimeout(() => setConfirmingDelete(false), 3000)
			return
		}
		void deleteDataset(dataset.id)
	}

	return (
		<div className={cn("flex items-center gap-3 px-3 py-2.5", pending && "opacity-60")}>
			<div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
				{dataset.format === "geojson"
					? <FileJson className="w-4 h-4 text-cyan-400" />
					: dataset.format === "image"
						? <Image className="w-4 h-4 text-cyan-400" />
						: <FileSpreadsheet className="w-4 h-4 text-cyan-400" />}
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-foreground truncate">{dataset.name}</div>
				<div className="text-xs text-muted-foreground truncate">
					{formatBytes(dataset.size_bytes)}
					{dataset.feature_count !== null && ` · ${dataset.feature_count.toLocaleString()} features`}
					{pending && " · uploading…"}
					{` · ${new Date(dataset.created_at).toLocaleDateString()}`}
				</div>
			</div>
			<button
				onClick={() => { void handleOpen() }}
				disabled={opening || pending}
				title="Open on this device"
				aria-label={`Open ${dataset.name}`}
				className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
			>
				{opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
			</button>
			{shareable && (
				<button
					onClick={() => { void handleShare() }}
					disabled={sharing}
					title={shareCopied ? "Share link copied" : "Create share link"}
					aria-label={`Share ${dataset.name}`}
					className={cn(
						"flex-shrink-0 p-2 rounded-lg transition-colors disabled:opacity-50",
						shareCopied ? "text-emerald-400" : "text-muted-foreground hover:text-foreground hover:bg-muted"
					)}
				>
					{sharing ? <Loader2 className="w-4 h-4 animate-spin" />
						: shareCopied ? <Check className="w-4 h-4" />
							: <Link2 className="w-4 h-4" />}
				</button>
			)}
			<button
				onClick={handleDelete}
				disabled={deleting}
				title={confirmingDelete ? "Click again to delete permanently" : "Delete from cloud storage"}
				aria-label={confirmingDelete ? `Confirm delete ${dataset.name}` : `Delete ${dataset.name}`}
				className={cn(
					"flex-shrink-0 p-2 rounded-lg transition-colors disabled:opacity-50",
					confirmingDelete
						? "text-red-500 bg-red-500/10"
						: "text-muted-foreground hover:text-red-400 hover:bg-muted"
				)}
			>
				{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
			</button>
		</div>
	)
}
